import { supabase } from "~/integrations/supabase/client";

// Frontend mirror of the finished-goods stock gate enforced server-side in
// supabase/functions/_shared/stockGuard.ts. Used to disable the "Generar
// factura" button before the request is even sent. The edge function remains
// the authoritative gate; this only drives UX.
//
// A factura can be emitted only for what is DISPONIBLE PARA LA VENTA =
// físico (products.stock) − facturado no despachado (get_reserved_not_dispatched).
// products.stock is físico and decrements on dispatch, so invoiced-not-dispatched
// boxes still sit in físico and must be netted out here.

// get_reserved_not_dispatched isn't in the generated types yet; thin typed wrapper.
type RpcReader = <TRow>(
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: TRow[] | null; error: { message: string } | null }>;

const rpcRead = supabase.rpc.bind(supabase) as unknown as RpcReader;

export interface InsufficientStockItem {
  productId: string;
  name: string;
  sku: string | null;
  required: number;
  available: number;
}

export interface FinishedGoodsStockCheck {
  ok: boolean;
  insufficient: InsufficientStockItem[];
}

// A product with no physical stock concept — a billed service or freight — never
// blocks a factura. Classified purely by category: grupo OR subgrupo = SERVICIOS
// (FLETE is categorized under SERVICIOS; do NOT fall back to the product name).
// KEEP IN SYNC with the Deno copy in supabase/functions/_shared/stockGuard.ts.
export function isNonStockService(p: {
  group_description?: string | null;
  subgroup_description?: string | null;
}): boolean {
  const grupo = (p.group_description ?? "").trim().toUpperCase();
  const subgrupo = (p.subgroup_description ?? "").trim().toUpperCase();
  return grupo === "SERVICIOS" || subgrupo === "SERVICIOS";
}

export async function checkFinishedGoodsStock(
  salesRequestId: string
): Promise<FinishedGoodsStockCheck> {
  // Test clients (clients.is_test) bypass the finished-goods stock gate so
  // demo/QA facturas can be emitted without real stock. Mirrors the server-side
  // bypass in create-factura / create-factura-picking. UX only — the edge
  // function is the authoritative gate.
  const { data: sr } = await supabase
    .from("sales_request")
    .select("client_id")
    .eq("id", salesRequestId)
    .maybeSingle();
  if (sr?.client_id) {
    const { data: client } = await supabase
      .from("clients")
      .select("is_test")
      .eq("id", sr.client_id)
      .maybeSingle();
    if (client?.is_test) return { ok: true, insufficient: [] };
  }

  const { data: items, error: itemsError } = await supabase
    .from("sales_request_items")
    .select("product_id, quantity")
    .eq("sales_request_id", salesRequestId)
    .is("deleted_at", null)
    .not("product_id", "is", null);

  if (itemsError) {
    throw new Error(`No se pudo validar el stock: ${itemsError.message}`);
  }

  const requiredByProduct = new Map<string, number>();
  for (const item of items ?? []) {
    if (!item.product_id) continue;
    requiredByProduct.set(
      item.product_id,
      (requiredByProduct.get(item.product_id) ?? 0) + Number(item.quantity ?? 0)
    );
  }

  const productIds = [...requiredByProduct.keys()];
  if (productIds.length === 0) return { ok: true, insufficient: [] };

  const { data: products, error: productsError } = await supabase
    .from("products")
    .select("id, short_name, sku, stock, group_description, subgroup_description")
    .in("id", productIds);

  if (productsError) {
    throw new Error(`No se pudo validar el stock: ${productsError.message}`);
  }

  const infoById = new Map<
    string,
    { stock: number; name: string; sku: string | null; isService: boolean }
  >();
  for (const p of products ?? []) {
    infoById.set(p.id, {
      stock: Number(p.stock ?? 0),
      name: p.short_name ?? p.id,
      sku: p.sku ?? null,
      isService: isNonStockService(p),
    });
  }

  // "Facturado no despachado" per product — netted out to get disponible.
  const { data: reservedRows, error: reservedError } = await rpcRead<{
    product_id: string;
    reserved_qty: number;
  }>("get_reserved_not_dispatched", {});
  if (reservedError) {
    throw new Error(`No se pudo validar el stock: ${reservedError.message}`);
  }
  const reservedById = new Map<string, number>();
  for (const r of reservedRows ?? [])
    reservedById.set(r.product_id, Number(r.reserved_qty) || 0);

  const insufficient: InsufficientStockItem[] = [];
  for (const [productId, rawRequired] of requiredByProduct) {
    const required = Math.round(rawRequired);
    if (required <= 0) continue;
    const info = infoById.get(productId);
    if (info?.isService) continue; // services carry no stock — never block
    const available = (info?.stock ?? 0) - (reservedById.get(productId) ?? 0);
    if (available < required) {
      insufficient.push({
        productId,
        name: info?.name ?? productId,
        sku: info?.sku ?? null,
        required,
        available,
      });
    }
  }

  return { ok: insufficient.length === 0, insufficient };
}
